import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Descriptions, Drawer, Space, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import * as api from '@apis/group-monitoring';
import EnrollmentSourceDrawer from '@components/grouping-agent/EnrollmentSourceDrawer';
import GroupEnrollmentMembersTable from '@components/grouping-agent/GroupEnrollmentMembersTable';

const { Text, Title } = Typography;

interface Props {
  groupId: string | null;
  open: boolean;
  onClose: () => void;
}

const GroupMonitoringDetailDrawer: React.FC<Props> = ({ groupId, open, onClose }) => {
  const [sourceScheduleId, setSourceScheduleId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['group-monitoring-detail', groupId],
    queryFn: () => api.getGroupMonitoringGroupDetail(groupId!),
    enabled: open && !!groupId,
  });

  const detail = query.data?.data;
  const group = detail?.group;
  const members = detail?.members || [];

  return (
    <>
      <Drawer
        title={group ? `그룹 상세 · ${group.name}` : '그룹 상세'}
        open={open}
        onClose={onClose}
        width={1000}
        destroyOnClose
        styles={{ body: { paddingBottom: 24 } }}>
        {query.isLoading && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        )}
        {group && !query.isLoading && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              program_schedule_groups · 결제건 program_schedules_view (읽기)
            </Text>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="파트너">{group.partnerName || '-'}</Descriptions.Item>
              <Descriptions.Item label="상품">{group.productName || '-'}</Descriptions.Item>
              <Descriptions.Item label="회차">{group.optionName || '-'}</Descriptions.Item>
              <Descriptions.Item label="수업일">
                {group.startDate ? dayjs(group.startDate).format('YYYY-MM-DD HH:mm') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="현재인원">{group.currentCount}</Descriptions.Item>
              <Descriptions.Item label="최소인원">{group.studentsMin}</Descriptions.Item>
              <Descriptions.Item label="최대인원">
                {group.studentsMax < 0 ? '제한없음' : group.studentsMax}
              </Descriptions.Item>
              <Descriptions.Item label="상태">
                <Tag color={group.groupStatus === 'complete' ? 'green' : 'orange'}>
                  {group.groupStatus === 'complete' ? '완료' : '보충 필요'}
                </Tag>
              </Descriptions.Item>
              {group.latestAgentRunId && (
                <Descriptions.Item label="최근 에이전트" span={2}>
                  <Button type="link" href={`/grouping-agent-runs/${group.latestAgentRunId}`}>
                    {group.latestAgentRunId.slice(0, 12)}…
                  </Button>
                </Descriptions.Item>
              )}
            </Descriptions>

            <div>
              <Title level={5} style={{ marginBottom: 12 }}>
                참가자 ({members.length}명)
              </Title>
              <GroupEnrollmentMembersTable
                members={members}
                onOpenSource={(scheduleId) => setSourceScheduleId(scheduleId)}
                classStartDate={group.startDate}
              />
            </div>
          </Space>
        )}
      </Drawer>
      <EnrollmentSourceDrawer
        scheduleId={sourceScheduleId}
        open={!!sourceScheduleId}
        onClose={() => setSourceScheduleId(null)}
        apiSource="group-monitoring"
      />
    </>
  );
};

export default GroupMonitoringDetailDrawer;
